import ButtonBackHeader from "@/components/ui/Button/ButtonBackHeader";
import Navigation from "@/components/Navigation/Navigation";
import MenuServingTimePicker from "@/components/Parent/MenuServingTimePicker";
import OrderingHeader from "@/components/Parent/OrderingHeader";
import StickyReviewCartPay from "@/components/Parent/StickyReviewCartPay";
import nf, { nfRp } from "@/constants/nf";
import {
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";

import QuantityControl from "./QuantityControl";
import ServingItem from "./ServingItem";
import { getCutOffTime } from "@/helper/getCutOffTime";
import getRemainingTime from "@/helper/getRemainingTime";
import {
  EMPTY_CART_ITEMS,
  cartQuantityTotal,
  linesForChild,
  linesForChildAndDate,
  mealDateIso,
} from "@/helper/cartFamily";
import { canOrderForChild } from "@/helper/dashboardStatus";
import { pathMenu } from "@/constants/routeName";
import { getDiscountItem } from "@/store/slices/cartSlice";
import { getServingTimes } from "@/store/slices/contentSlice";
import WarningServingTime from "@/components/Modal/WarningServingTime";
import { wTextDO1 } from "@/constants/wTextST";

const AddOnModifier = ({ data, selected, onCheck, parentStyle }) => {
  if (parentStyle) {
    return (
      <label className="addon-option">
        <span>{data.name}</span>
        <small>
          {data.price
            ? `${Math.sign(data.price) !== -1 ? "+" : ""}${nfRp(data.price)}`
            : "Free"}
        </small>
        <Checkbox
          checked={!!selected}
          onChange={() => onCheck(data)}
          inputProps={{
            "aria-label": `${data.name} ${data.price ? nfRp(data.price) : "Free"}`,
          }}
        />
      </label>
    );
  }
  return (
    <div className="carts add-ons">
      <span>{data.name}</span>
      <span>
        {data.price
          ? `${Math.sign(data.price) !== -1 ? "+" : ""}${nf(data.price)}`
          : "Free"}
      </span>
      <Checkbox
        className="ck-table"
        checked={!!selected}
        onChange={() => onCheck(data)}
        inputProps={{ "aria-label": data.name }}
      />
    </div>
  );
};

export default function Item() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const item = useSelector((state) => state.cart.selectedItem);
  const servingTimes = useSelector((state) => state.content.servingTimes);
  const orderDate = useSelector((state) => state.cart.orderDate);
  const menuServingTimeId = useSelector((state) => state.cart.menuServingTimeId);
  const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;
  const utcOffset = useSelector((state) => state.content.utcOffset);
  const outlet = useSelector((state) => state.content.outlet);
  const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
  const discountCustomer = useSelector((state) => {
    const disc = state.cart.discount;
    const forId = state.cart.discountForCustomerId;
    const selectedId = state.auth.selectedCustomer?.id;
    if (disc && forId != null && selectedId != null && Number(forId) === Number(selectedId)) {
      return disc;
    }
    if (disc && forId == null) return disc;
    return null;
  });

  const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
  const accountType = useSelector((state) => state.auth.accountType);
  const isParent = accountType === "parent";

  const [addOns, setAddOns] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [servingTime, setServingTime] = useState("");
  const [amount, setAmount] = useState(1);
  const [showWarning, setShowWarning] = useState(false);
  const [notes, setNotes] = useState(item?.notes || "");

  const add_ons = item?.add_ons;
  const modifier = item?.modifiers;

  const isA = add_ons?.length > 0;
  const isM = modifier?.length > 0;

  // const onCheck = (key, id, checked) => {
  // 	dispatch({ type: 'cart/setSelectedItemKey', payload: { key, id, checked } });
  // };
  const onAddOns = (itm) => {
    if (addOns.find((a) => a.id === itm.id)) {
      setAddOns((prevState) => prevState.filter((a) => a.id !== itm.id));
    } else {
      setAddOns((prevState) => prevState.concat([itm]));
    }
  };
  const onAddModifiers = (itm) => {
    if (modifiers.find((a) => a.id === itm.id)) {
      setModifiers((prevState) => prevState.filter((a) => a.id !== itm.id));
    } else {
      setModifiers((prevState) => prevState.concat([itm]));
    }
  };
  const handleChangeServing = (event) => {
    const nextId = event.target.value;
    setServingTime(nextId);
    dispatch({ type: "cart/setMenuServingTimeId", payload: nextId });
    const childLines = selectedCustomer?.id
      ? linesForChild(cartItems, selectedCustomer.id)
      : [];
    // After meals are in the cart, switching Lunch/Snack returns to Menu (PA2-05).
    if (childLines.length > 0) {
      dispatch({ type: "cart/resetMenuBrowse" });
      navigate(pathMenu);
    }
  };

  useEffect(() => {
    if (menuServingTimeId != null && servingTimes?.length) {
      const match = servingTimes.find((s) => Number(s.id) === Number(menuServingTimeId));
      if (match) setServingTime(String(match.id));
    }
  }, [menuServingTimeId, servingTimes]);
  const changeAmount = (key) => {
    setAmount((prevState) => {
      if (key === "plus") {
        return prevState + 1;
      }
      if (prevState === 1) return 1;
      return prevState - 1;
    });
  };
  const getAddOnsModifier = () => {
    const newAddOns = addOns.map((a) => {
      return { id: uuidv4(), add_on: { ...a } };
    });
    const newModifiers = modifiers.map((a) => {
      return { id: uuidv4(), modifier: { ...a } };
    });
    return { addOns: newAddOns, modifiers: newModifiers };
  };
  const getSTime = (s) => {
    const { oCutOff, sT } = getCutOffTime(s, orderDate, utcOffset);
    const remainingDayTime = getRemainingTime(oCutOff);
    let isDisable = disableOrder || remainingDayTime < 0 ? true : false;

    return { isDisable, oCutOff, sT };
  };

  const disableOrder =
    "use_minimum_balance_limit" in outlet &&
    outlet?.use_minimum_balance_limit &&
    "minimum_balance_limit" in outlet &&
    +outlet?.minimum_balance_limit > +selectedCustomer.balance;

  const onAddToCart = () => {
    if (!canOrderForChild(selectedCustomer)) {
      toast.error("This student is inactive for your family. You cannot add items.");
      return;
    }
    const { addOns, modifiers } = getAddOnsModifier();
    const sTime = servingTime
      ? servingTimes.find((s) => +s.id === +servingTime)
      : null;
    if (!sTime) return;
    const { isDisable } = getSTime(sTime);

    if (isDisable) {
      setShowWarning(true);
      return;
    }
    dispatch({
      type: "cart/setItems",
      payload: {
        ...item,
        quantity: amount,
        is_void: false,
        modifiers: modifiers,
        add_ons: addOns,
        serving_time: sTime,
        date: orderDate,
        notes: notes,
        uid: uuidv4(),
        ownerCustomerId: selectedCustomer?.id,
      },
    });
    dispatch(getServingTimes({ outlet: selectedOutlet?.id, max_size: true }));
    navigate(-1);
  };

  let totalPrice = (+item?.selling_price || 0) * amount;
  let totalAddOns = 0;
  let discValue = 0;
  if (addOns.length) {
    totalAddOns = addOns.reduce((a, b) => +a + +b.price * amount, 0);
    totalPrice += totalAddOns;
  }

  if (discountCustomer) {
    discValue =
      getDiscountItem(+item?.selling_price || 0, amount, discountCustomer) || 0;
    discValue += getDiscountItem(totalAddOns, 1, discountCustomer) || 0;
    totalPrice -= discValue;
  }

  useEffect(() => {
    if (!item) {
      navigate(-1);
    }
  }, [item, navigate]);

  let discVal = 0;
  if (discountCustomer) {
    const { type, value } = discountCustomer;
    discVal = `${value}${type === "Percentage" ? "%" : ""}`;
  }
  const uBalance = +selectedCustomer?.balance || 0;
  const showTextWarning = uBalance < 0;
  const cartQty = cartQuantityTotal(cartItems);
  const servingName =
    servingTimes?.find((s) => Number(s.id) === Number(servingTime))?.name || "Meal";
  const childFirst = selectedCustomer?.member?.name?.split(/\s+/)[0] || "Child";
  const dateIso = mealDateIso(orderDate);
  const existingForServing =
    selectedCustomer?.id != null && servingTime
      ? linesForChildAndDate(cartItems, selectedCustomer.id, dateIso).filter(
          (line) => Number(line.serving_time?.id) === Number(servingTime)
        )
      : [];
  const existingQty = existingForServing.reduce(
    (n, line) => n + (Number(line.quantity) || 0),
    0
  );

  if (isParent) {
    return (
      <div className="parent-ui">
        <OrderingHeader title="Meal details" backTo="menu">
          <div className="product-top">
            <h1>{item?.name}</h1>
            <strong>{nfRp(+item?.selling_price || 0)}</strong>
          </div>
          {item?.thumbnail ? (
            <img
              src={item.thumbnail}
              alt={item?.name}
              className="product-photo"
            />
          ) : (
            <div
              className="product-photo"
              role="img"
              aria-label={item?.name || "Meal"}
            />
          )}
          {item?.description && <p className="muted">{item.description}</p>}

          {(isA || isM) && (
            <section className="product-section">
              <h3>
                Add-ons <small className="muted">· Optional</small>
              </h3>
              {isA &&
                add_ons.map((a) => {
                  const selected = addOns.find((m) => m.id === a.id);
                  return (
                    <AddOnModifier
                      key={`add-ons-${a?.id}-${item?.uid}`}
                      data={a}
                      onCheck={onAddOns}
                      selected={selected}
                      parentStyle
                    />
                  );
                })}
              {isM &&
                modifier.map((m) => {
                  const selected = modifiers.find((a) => a.id === m.id);
                  return (
                    <AddOnModifier
                      key={`modifier-${m?.id}-${item?.uid}`}
                      data={m}
                      onCheck={onAddModifiers}
                      selected={selected}
                      parentStyle
                    />
                  );
                })}
            </section>
          )}

          <label className="product-section">
            <strong>
              Notes <small className="muted">· Optional</small>
            </strong>
            <textarea
              className="notes-field"
              placeholder="Add a note for this item"
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              aria-label="Notes · Optional"
            />
          </label>

          {existingQty > 0 && (
            <p className="notice" role="status">
              {childFirst} already has {existingQty} item
              {existingQty === 1 ? "" : "s"} for {servingName}. This adds another
              item.
            </p>
          )}

          <div className="row product-section">
            <strong>Purchase amount</strong>
            <div className="qty">
              <button
                type="button"
                aria-label="Decrease quantity"
                disabled={amount <= 1}
                onClick={() => changeAmount("minus")}
              >
                −
              </button>
              <span>{amount}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => changeAmount("plus")}
              >
                +
              </button>
            </div>
          </div>

          <div className="selection-summary">
            <strong>
              {selectedCustomer?.member?.name || "Child"} · {servingName}
            </strong>
            {dayjs(orderDate).format("dddd, D MMMM YYYY")}
            <div className="row">
              <span>Discount</span>
              <span>{nfRp(discValue || 0)}</span>
            </div>
            <div className="row">
              <span>Item total</span>
              <strong>{nfRp(totalPrice)}</strong>
            </div>
          </div>

          {disableOrder && <span className="text-red-300">{wTextDO1}</span>}
          {showTextWarning && (
            <p className="muted">
              Deposit is insufficient ({nfRp(+selectedCustomer.balance || 0)}).
              Please top up.
            </p>
          )}

          <button
            type="button"
            className="main full"
            onClick={onAddToCart}
            disabled={amount === 0 || !servingTime}
          >
            Add to cart · {nfRp(totalPrice)}
          </button>
          {cartQty > 0 && <StickyReviewCartPay />}
        </OrderingHeader>
        <Navigation />
        <WarningServingTime open={showWarning} onClose={() => setShowWarning(false)} />
      </div>
    );
  }

  return (
    <div className="app-container app-page">
      <div className="relative flex flex-col flex-1 gap-6 p-4 pb-24">
        <ButtonBackHeader />
        <MenuServingTimePicker />
        <div className="relative flex flex-col items-center justify-center">
          <span className="font-bold">{item?.name}</span>
          <div>
            <span className="font-bold">{nf(+item?.selling_price)}</span>
          </div>
        </div>
        <img src={item?.thumbnail} alt={item?.name} className="w-full h-auto max-h-[65vh] object-cover" />

        {(isA || isM) && (
          <div className="flex flex-col">
            {isA &&
              add_ons.map((a) => {
                const selected = addOns.find((m) => m.id === a.id);
                return (
                  <AddOnModifier
                    key={`add-ons-${a?.id}-${item?.uid}`}
                    data={a}
                    onCheck={onAddOns}
                    selected={selected}
                  />
                );
              })}
            {isM &&
              modifier.map((m) => {
                const selected = modifiers.find((a) => a.id === m.id);
                return (
                  <AddOnModifier
                    key={`modifier-${m?.id}-${item?.uid}`}
                    data={m}
                    onCheck={onAddModifiers}
                    selected={selected}
                  />
                );
              })}
          </div>
        )}
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
        <FormControl>
          <FormLabel id="demo-controlled-radio-buttons-group">
            Serving Times
          </FormLabel>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={servingTime}
            onChange={handleChangeServing}
          >
            {servingTimes?.map((s) => {
              const { isDisable, oCutOff, sT } = getSTime(s);
              const sTime = sT;

              return (
                <ServingItem
                  key={s.id}
                  item={s}
                  oCutOff={oCutOff}
                  isDisable={isDisable}
                  servingTime={sTime ? format(sTime, "HH:mm OOOO") : ""}
                  disableOrder={disableOrder}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
        {disableOrder && <span className="text-red-300">{wTextDO1}</span>}
        <div className="flex justify-between">
          <span>Purchase Amount</span>
          <QuantityControl
            amount={amount}
            changeAmount={changeAmount}
            disabled={amount === 0}
          />
        </div>
        <div className="flex justify-between">
          <span>Order Date</span>
          <span>{dayjs(orderDate).format("dddd, DD MMMM YYYY")}</span>
        </div>
        {!!discountCustomer && (
          <div className="flex justify-between">
            <span>Discount {`(${discountCustomer.name})`}</span>
            {!!discVal && <span>{discVal}</span>}
          </div>
        )}
        {showTextWarning && (
          <span>
            Sorry, your deposit is insufficient. After your recent order your
            remaining balance is{" "}
            <span className="text-red-500">
              {nf(+selectedCustomer.balance || 0)}
            </span>
            . Please contact our admin for your top up. Kind regards Jungle
            Toppings
          </span>
        )}
        <button
          className="flex flex-col btn-actions"
          onClick={onAddToCart}
          disabled={amount === 0 || !servingTime}
        >
          <span className="ml-1">Add To Cart {nf(totalPrice)}</span>
          {discValue > 0 && (
            <span className="text-sm font-bold">disc: -{nf(discValue)}</span>
          )}
        </button>
      </div>
      <WarningServingTime
        open={showWarning}
        onClose={() => setShowWarning(false)}
      />
    </div>
  );
}
